import { Button, Flex, notification, Popconfirm } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { postDevFrontDebug } from "tm-shared/api";
import { deepParseJson } from "tm-shared/utils";

import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useSW } from "@/hooks/useSW";
import {
    StyledUserCard,
    UserInfoDescriptionText,
    UserInfoTitle,
} from "@/pages/Profile/MainInformation/Styled";
import {
    updateManualOfflineStatus,
    updateOfflineStatus,
} from "@/store/slices/offline/offline.slice";
import { toggleTheme } from "@/store/slices/theme/theme.slice";
import { isOnline } from "@/utils/network/isOnline";

import {
    StyledContainerAccount,
    StyledDisconnectOutlined,
    StyledDivider,
    StyledMailOutlined,
    StyledMoonOutlined,
    StyledRightOutlined,
    StyledSwitch,
    StyledSyncOutlined,
    StyledTwoColumnsGrid,
    StyledUserOutlined,
    StyledUserSettings,
} from "./Styled";

const storeResetWarning =
    "Після оновлення поточний прогрес у додатку буде скинуто.";

export const UserSettings = () => {
    const { type } = useAppSelector((state) => state.theme);
    const { offlineMode } = useAppSelector((state) => state.offline);

    const [isSendingState, setIsSendingState] = useState(false);
    const [offlineLoading, setOfflineLoading] = useState(false);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleThemeChange = () => {
        dispatch(toggleTheme());
    };

    const handleNetworkChange = async () => {
        if (offlineMode) {
            setOfflineLoading(true);
            const online = await isOnline(5000);
            setOfflineLoading(false);

            if (!online) {
                notification.warning({
                    message: "Перейти в онлайн наразі неможливо.",
                    description:
                        "Немає підключення до інтернету або з’єднання занадто слабке.",
                });
                return;
            }
        }

        dispatch(dispatch(updateOfflineStatus(!offlineMode)));
        dispatch(dispatch(updateManualOfflineStatus(!offlineMode)));
    };

    const handleSendState = async () => {
        setIsSendingState(true);
        const stringifiedState = localStorage.getItem("persist:root");
        const debugInfo = deepParseJson(stringifiedState) as Record<
            string,
            unknown
        >;

        const user = debugInfo?.user as { id?: string } | undefined;

        await postDevFrontDebug({
            user: user?.id || "No data",
            debugInfo,
        });

        setIsSendingState(false);
    };

    const { needRefresh, applyUpdate, checkUpdate, checkLoading, storeReset } =
        useSW();

    return (
        <StyledUserSettings>
            <UserInfoTitle>Налаштування</UserInfoTitle>

            <StyledUserCard>
                <StyledContainerAccount
                    onClick={() => navigate("/profile/account")}
                >
                    <StyledUserOutlined />

                    <Flex vertical gap="12px">
                        <UserInfoTitle>Акаунт</UserInfoTitle>
                        <UserInfoDescriptionText>
                            Керування даними: ім’я, E-mail, номер телефону,
                            пароль
                        </UserInfoDescriptionText>
                    </Flex>
                    <StyledRightOutlined />
                </StyledContainerAccount>

                <StyledTwoColumnsGrid>
                    <Flex gap="12px" align="center">
                        <StyledMoonOutlined />
                        <UserInfoTitle>Темна тема</UserInfoTitle>
                    </Flex>

                    <StyledSwitch
                        checked={type === "dark"}
                        onChange={handleThemeChange}
                    />
                </StyledTwoColumnsGrid>

                <StyledTwoColumnsGrid>
                    <Flex gap="12px" align="center">
                        <StyledDisconnectOutlined />
                        <UserInfoTitle>Офлайн режим</UserInfoTitle>
                    </Flex>

                    <StyledSwitch
                        checked={offlineMode}
                        onChange={handleNetworkChange}
                        loading={offlineLoading}
                    />
                </StyledTwoColumnsGrid>

                <StyledDivider />

                <StyledTwoColumnsGrid>
                    <Flex gap="12px" align="center">
                        <StyledMailOutlined />
                        <UserInfoTitle>Стан додатку</UserInfoTitle>
                    </Flex>
                    <Popconfirm
                        title="Відправити стан додатку?"
                        description={
                            <>
                                Ви впевнені, що хочете надіслати стан додатку на
                                сервер?
                                <br />
                                Цю дію слід виконувати лише за наявності
                                стабільного інтернет-з&apos;єднання.
                            </>
                        }
                        onConfirm={handleSendState}
                        okText="Так"
                        cancelText="Ні"
                    >
                        <Button
                            type="primary"
                            loading={isSendingState}
                            disabled={offlineMode}
                        >
                            Надіслати
                        </Button>
                    </Popconfirm>
                </StyledTwoColumnsGrid>

                <Flex vertical gap={8}>
                    <StyledTwoColumnsGrid>
                        <Flex gap="12px" align="center">
                            <StyledSyncOutlined />

                            <UserInfoTitle>Оновлення</UserInfoTitle>
                        </Flex>

                        {needRefresh ? (
                            <Popconfirm
                                title={
                                    <Flex vertical gap={4}>
                                        <PrimaryText lineHeight="22px">
                                            Ви впевнені що готові оновити
                                            додаток?
                                        </PrimaryText>

                                        {needRefresh && storeReset && (
                                            <PrimaryText $textColor="error">
                                                {storeResetWarning}
                                            </PrimaryText>
                                        )}
                                    </Flex>
                                }
                                onConfirm={applyUpdate}
                                okText="Так"
                                cancelText="Ні"
                            >
                                <Button disabled={offlineMode} type="primary">
                                    Оновити
                                </Button>
                            </Popconfirm>
                        ) : (
                            <Button
                                onClick={checkUpdate}
                                loading={checkLoading}
                                type="primary"
                                disabled={offlineMode}
                            >
                                Перевірити
                            </Button>
                        )}
                    </StyledTwoColumnsGrid>

                    {needRefresh && storeReset && (
                        <PrimaryText
                            $textColor="error"
                            fontSize={16}
                            fontWeight="500"
                        >
                            {storeResetWarning}
                        </PrimaryText>
                    )}
                </Flex>
            </StyledUserCard>
        </StyledUserSettings>
    );
};
